import React, { useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";

export interface ReviewTrendDataPoint {
    date: string;
    [key: string]: string | number | null | undefined;
}

interface ReviewTrendChartProps {
    data: ReviewTrendDataPoint[];
    title: string;
    description?: string;
    lines: Array<{
        dataKey: string;
        name: string;
        color: string;
        strokeWidth?: number;
        strokeDasharray?: string;
    }>;
    yAxisFormatter?: (value: number) => string;
    tooltipFormatter?: (value: number) => string;
    height?: number;
    /** Optional shaded x-range (e.g. the selected period) drawn behind the lines, labelled in-chart (FR-020/FR-021). */
    highlight?: { from: string; to: string; label: string };
}

const ReviewTrendChart: React.FC<ReviewTrendChartProps> = ({
    data,
    title,
    description,
    lines,
    yAxisFormatter = (value) => value.toFixed(2),
    tooltipFormatter = (value) => value.toFixed(2),
    height = 400,
    highlight,
}) => {
    // Track which lines are visible (all visible by default)
    const [hiddenLines, setHiddenLines] = useState<Set<string>>(new Set());

    const handleLegendClick = (dataKey: string) => {
        setHiddenLines((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(dataKey)) {
                newSet.delete(dataKey);
            } else {
                newSet.add(dataKey);
            }
            return newSet;
        });
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>{title}</CardTitle>
                {description && <CardDescription>{description}</CardDescription>}
            </CardHeader>
            <CardContent>
                <div className="relative" style={{ height: `${height}px` }}>
                    {highlight && (
                        // Layout-independent marker for the shaded selected period. The
                        // Recharts ReferenceArea below is the real in-chart shading, but it
                        // resolves its axis scale asynchronously and renders nothing under
                        // jsdom — so the owned `review-trend-highlight` testid (which the
                        // page spec asserts on, per its "Recharts renders no <text> under
                        // jsdom" note) is emitted here where it is always present.
                        <span
                            data-testid="review-trend-highlight"
                            data-highlight-from={highlight.from}
                            data-highlight-to={highlight.to}
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-0"
                        />
                    )}
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="date" stroke="#888888" fontSize={12} tickLine={false} axisLine={{ stroke: "#e5e7eb" }} />
                            <YAxis
                                stroke="#888888"
                                fontSize={12}
                                tickLine={false}
                                axisLine={{ stroke: "#e5e7eb" }}
                                tickFormatter={yAxisFormatter}
                                domain={[4.0, 5.0]}
                            />
                            <Tooltip
                                formatter={(value: number | undefined, name: string | undefined) => [
                                    value !== undefined ? tooltipFormatter(value) : "N/A",
                                    name ?? "",
                                ]}
                                contentStyle={{
                                    backgroundColor: "white",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "6px",
                                    padding: "12px",
                                }}
                                labelStyle={{ fontWeight: "bold", marginBottom: "8px" }}
                            />
                            <Legend
                                wrapperStyle={{ paddingTop: "20px", cursor: "pointer" }}
                                onClick={(e) => {
                                    if (e.dataKey) {
                                        handleLegendClick(String(e.dataKey));
                                    }
                                }}
                                formatter={(value, entry) => {
                                    const isHidden = hiddenLines.has(String(entry.dataKey));
                                    return (
                                        <span
                                            style={{
                                                color: isHidden ? "#9ca3af" : "#000",
                                                textDecoration: isHidden ? "line-through" : "none",
                                            }}
                                        >
                                            {value}
                                        </span>
                                    );
                                }}
                            />
                            {highlight && (
                                <ReferenceArea
                                    x1={highlight.from}
                                    x2={highlight.to}
                                    ifOverflow="extendDomain"
                                    fill="#6366f1"
                                    fillOpacity={0.08}
                                    stroke="none"
                                    label={{ value: highlight.label, position: "insideTop", fill: "#4b5563", fontSize: 12 }}
                                    // Visual shading only; the owned testid lives on the layout-independent
                                    // marker above (the ReferenceArea renders nothing under jsdom).
                                    shape={(props: { x?: number; y?: number; width?: number; height?: number }) => (
                                        <rect
                                            x={props.x}
                                            y={props.y}
                                            width={props.width}
                                            height={props.height}
                                            fill="#6366f1"
                                            fillOpacity={0.08}
                                        />
                                    )}
                                />
                            )}
                            {lines.map((line) => (
                                <Line
                                    key={line.dataKey}
                                    type="monotone"
                                    dataKey={line.dataKey}
                                    name={line.name}
                                    stroke={line.color}
                                    strokeWidth={line.strokeWidth || 2}
                                    strokeDasharray={line.strokeDasharray}
                                    dot={{ r: 4, fill: line.color }}
                                    activeDot={{ r: 6 }}
                                    hide={hiddenLines.has(line.dataKey)}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
};

export default ReviewTrendChart;
